// App state: the active save slot as signals, debounced autosave (500 ms), progress and unlock rules
// (rebuild.md §4.6), and settings applied to the document.
import { computed, effect, signal } from '@preact/signals';
import { CHAPTERS } from '@content/chapters.ts';
import type { Tier } from '@prompts/types.ts';
import { defaultSettings, newSave, type CheckpointSave, type Save, type Settings, type StageResult } from '@save/schema.ts';
import { loadOrCreate, openStore, readMeta, saveSlot, writeMeta } from '@save/store.ts';
import { loadArtManifest } from '@ui/Art.tsx';

export const save = signal<Save>(newSave(1, new Date().toISOString()));
export const ready = signal(false);
export const settings = computed<Settings>(() => save.value.settings ?? defaultSettings());
export const tier = computed<Tier>(() => save.value.tier);

let timer: ReturnType<typeof setTimeout> | null = null;
function scheduleSave(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => {
    timer = null;
    void saveSlot(save.value);
  }, 500);
}

/** Flush any pending autosave now (on page hide). */
export async function flushSave(): Promise<void> {
  if (timer) {
    clearTimeout(timer);
    timer = null;
    await saveSlot(save.value);
  }
}

export function updateSave(fn: (s: Save) => Save): void {
  save.value = fn(save.value);
  scheduleSave();
}

export function updateSettings(patch: Partial<Settings>): void {
  updateSave((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
}

export function setTier(t: Tier): void {
  updateSave((s) => ({ ...s, tier: t }));
}

export async function boot(): Promise<void> {
  void loadArtManifest();
  await openStore();
  const meta = await readMeta();
  save.value = await loadOrCreate(meta.activeSlot);
  ready.value = true;
}

export async function switchSlot(slot: 1 | 2 | 3): Promise<void> {
  await flushSave();
  await writeMeta({ activeSlot: slot });
  save.value = await loadOrCreate(slot);
}

// ---------------------------------------------------------------- progress

export const totalCommendations = computed(() => {
  // the Home screen shows the best tier's total for each stage
  let n = 0;
  for (const byTier of Object.values(save.value.results)) {
    let best = 0;
    for (const r of Object.values(byTier)) best = Math.max(best, r.commendations.filter(Boolean).length);
    n += best;
  }
  return n;
});

export const blackoutUnlocked = computed(() => totalCommendations.value >= 30);

export function isUnlocked(stageId: string): boolean {
  if (stageId === '_test') return true;
  const s = save.value;
  return s.classroom || s.unlocked.includes(stageId) || stageId === 'prologue';
}

/** ECHO's upgrade-allocation screen (§4.6): all 51 commendations on Tier-1 · Permit. */
export const echoUpgradeUnlocked = computed(() => Object.values(save.value.results).reduce((n, byTier) => n + (byTier.permit?.commendations.filter(Boolean).length ?? 0), 0) >= 51);

export function isFiled(stageId: string): boolean {
  return Object.keys(save.value.results[stageId] ?? {}).length > 0;
}

export function recordResult(stageId: string, t: Tier, r: StageResult): void {
  updateSave((s) => {
    const prev = s.results[stageId]?.[t];
    // keep the best: more commendations, then fewer mistakes
    const better = !prev || r.commendations.filter(Boolean).length >= prev.commendations.filter(Boolean).length;
    const results = { ...s.results, [stageId]: { ...(s.results[stageId] ?? {}), ...(better ? { [t]: r } : {}) } };
    const idx = CHAPTERS.findIndex((c) => c.id === stageId);
    const nextId = CHAPTERS[idx + 1]?.id;
    const unlocked = new Set(s.unlocked);
    if (stageId !== '_test') unlocked.add(stageId);
    if (nextId) unlocked.add(nextId);
    const entries = new Set(s.notebook.entries);
    if (stageId !== '_test') entries.add(stageId);
    const { checkpoint: _drop, ...rest } = s;
    void _drop;
    return { ...rest, results, unlocked: [...unlocked], notebook: { ...s.notebook, entries: [...entries] } };
  });
}

/** A Lab Practice set completed (§4.9). Nothing is filed; the Practice screen ticks it. */
export function recordPractice(id: string): void {
  if (save.value.practice.includes(id)) return;
  updateSave((s) => ({ ...s, practice: [...s.practice, id] }));
}

/** A Field Guide Drill completed (§5.3). */
export function recordDrill(id: string): void {
  if (save.value.notebook.drillsDone.includes(id)) return;
  updateSave((s) => ({ ...s, notebook: { ...s.notebook, drillsDone: [...s.notebook.drillsDone, id] } }));
}

/** A Monday revision set scored (§4.9); the best score is kept. */
export function recordMonday(setId: string, score: number, of: number): void {
  const prev = save.value.monday[setId];
  if (prev && prev.best >= score) return;
  updateSave((s) => ({ ...s, monday: { ...s.monday, [setId]: { best: score, of, at: new Date().toISOString() } } }));
}

export const hasFlag = (flag: string): boolean => save.value.flags.includes(flag);

export function setFlag(flag: string): void {
  if (hasFlag(flag)) return;
  updateSave((s) => ({ ...s, flags: [...s.flags, flag] }));
}

/** Monday is open after the Epilogue (§4.6), or in Classroom mode. */
export const mondayUnlocked = computed(() => save.value.classroom || Object.keys(save.value.results.epilogue ?? {}).length > 0);

export function setCheckpoint(cp: CheckpointSave | undefined): void {
  updateSave((s) => {
    if (!cp) {
      const { checkpoint: _drop, ...rest } = s;
      void _drop;
      return rest;
    }
    return { ...s, checkpoint: cp };
  });
}

export function markWatched(excerptId: string): void {
  if (save.value.watched.includes(excerptId)) return;
  updateSave((s) => ({ ...s, watched: [...s.watched, excerptId] }));
}

// ---------------------------------------------------------------- settings → document

export function applySettingsToDocument(): () => void {
  return effect(() => {
    const st = settings.value;
    const root = document.documentElement;
    root.dataset.contrast = st.highContrast ? 'high' : 'normal';
    root.dataset.font = st.dyslexiaFont ? 'dyslexic' : 'default';
    root.dataset.motion = st.reduceMotion === 'system' ? 'system' : st.reduceMotion === 'on' ? 'reduced' : 'full';
    root.style.setProperty('--text-scale', String(st.textScale));
    root.dataset.captions = st.captionSize;
  });
}

/** True when animations should be reduced (setting or OS preference). */
export function reducedMotion(): boolean {
  const m = settings.value.reduceMotion;
  if (m === 'on') return true;
  if (m === 'off') return false;
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}
